import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  // CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { DataGrid, GridToolbar, GridActionsCellItem } from "@mui/x-data-grid";
import { Add, Edit, Delete } from "@mui/icons-material";
import LockIcon from "@mui/icons-material/Lock";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import { useTranslation } from "react-i18next";
import { fetchJson } from "../../../utils/fetchJson";
import { UserContext } from "../../../contexts/UserContext";
// import ErrorBoundary from "../../../components/ErrorBoundary";

export default function SuperAdminsPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [hasPermission, setHasPermission] = useState(false);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState({ main: true, schools: true });
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);


  const loadData = React.useCallback(async () => {
    if (!hasPermission) return;
    setLoading((prev) => ({ ...prev, main: true }));
    try {
      const data = await fetchJson("/super-admins", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setSuperAdmins(data);
    } catch (e) {
      setError(t("superAdmins.errors.load") || "Failed to load super admins");
    } finally {
      setLoading((prev) => ({ ...prev, main: false }));
    }
  }, [t, hasPermission]);

  const loadSchools = React.useCallback(async () => {
    if (!hasPermission) return;
    setLoading((prev) => ({ ...prev, schools: true }));
    try {
      const data = await fetchJson("/schools", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setSchools(data.schools || []);
    } catch (e) {
      setError(t("superAdmins.errors.schools") || "Failed to load schools");
    } finally {
      setLoading((prev) => ({ ...prev, schools: false }));
    }
  }, [t, hasPermission]);

  useEffect(() => {
    const checkPermissions = async () => {
      if (!user) {
        setHasPermission(false);
        return;
      }

      const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
      const allowed = user.role === 'SystemAdmin' || !perms || perms.includes('*') || perms.includes('users-access');
      setHasPermission(allowed);
    };

    checkPermissions();
  }, [user]);

  const canFeature = React.useCallback((permKey) => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes(permKey);
  }, [user]);

  const canManageUsersAccess = canFeature('users-access');

  useEffect(() => {
    if (hasPermission) {
      loadData();
    }
  }, [loadData, hasPermission]);

  useEffect(() => {
    if (hasPermission) {
      loadSchools();
    }
  }, [loadSchools, hasPermission]);

  const handleEdit = (admin) => {
    setCurrentAdmin(admin);
    setSelectedSchools(admin.schools?.map((s) => s.id) || []);
    setOpenDialog(true);
  };

  const handleCreate = () => {
    setCurrentAdmin({
      name: "",
      email: "",
      status: "active",
    });
    setSelectedSchools([]);
    setOpenDialog(true);
  };

  const handleDelete = (id) => {
    const admin = superAdmins.find((a) => a.id === id);
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!adminToDelete) return;
    try {
      await fetchJson(`/super-admins/${adminToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      loadData();
    } catch (e) {
      setError(t("superAdmins.errors.delete") || "Failed to delete super admin");
    } finally {
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
    }
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...currentAdmin,
        schoolIds: selectedSchools,
      };

      if (currentAdmin.id) {
        await fetchJson(`/super-admins/${currentAdmin.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: JSON.stringify(data),
        });
      } else {
        await fetchJson("/super-admins", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: JSON.stringify(data),
        });
      }

      setOpenDialog(false);
      loadData();
    } catch (e) {
      setError(t("superAdmins.errors.save") || "Failed to save super admin");
    }
  };

  const columns = [
    {
      field: "name",
      headerName: t("superAdmins.columns.name") || "Name",
      flex: 1,
      renderCell: (params) => (
        params?.row ? (
          <Stack>
            <Typography fontWeight="medium">{params.value}</Typography>
            <Typography variant="body2" color="textSecondary">
              {params.row.email}
            </Typography>
          </Stack>
        ) : null
      ),
    },
    // Tier column removed
    {
      field: "status",
      headerName: t("superAdmins.columns.status") || "Status",
      width: 120,
      renderCell: (params) => (
        params?.row ? (
          <Chip
            label={params.value}
            color={params.value === "active" ? "success" : "error"}
            variant="outlined"
            size="small"
          />
        ) : null
      ),
    },
    {
      field: "schools",
      headerName: t("superAdmins.columns.schools") || "Schools",
      flex: 2,
      renderCell: (params) => (
        params?.row ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {Array.isArray(params.value) && params.value.map((school) => (
              <Chip
                key={school.id}
                label={school.name}
                size="small"
                sx={{ mb: 0.5 }}
              />
            ))}
          </Stack>
        ) : null
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: t("superAdmins.columns.actions") || "Actions",
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label={t("common.edit")}
          onClick={() => handleEdit(params?.row)}
          disabled={!canManageUsersAccess}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label={t("common.delete")}
          onClick={() => handleDelete(params?.id)}
          showInMenu
          disabled={!canManageUsersAccess}
        />,
      ],
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={1}
        mb={3}
      >
        {!hasPermission ? (
          <Box p={3}>
            <Alert 
              severity="error" 
              icon={<LockIcon />}
              action={
                <Typography variant="body2" color="text.secondary">
                  Contact your administrator for access
                </Typography>
              }
            >
              Access Denied: You do not have Super Admin permissions to manage Super Admins.
            </Alert>
          </Box>
        ) : (
          <>
            <Typography variant="h4" gutterBottom>
              {t("superAdmins.title") || "Super Admins"}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              disabled={loading.main || !canManageUsersAccess}
            >
              {t("superAdmins.addButton") || "Add Super Admin"}
            </Button>
          </>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={superAdmins}
              columns={columns}
              loading={loading.main}
              components={{ Toolbar: GridToolbar }}
              getRowId={(row) => row.id}
              disableSelectionOnClick
              localeText={{
                toolbarDensity: t("datagrid.density"),
                toolbarDensityLabel: t("datagrid.densityLabel"),
                toolbarDensityCompact: t("datagrid.compact"),
                toolbarDensityStandard: t("datagrid.standard"),
                toolbarDensityComfortable: t("datagrid.comfortable"),
                toolbarExport: t("datagrid.export"),
                toolbarExportCSV: t("datagrid.exportCSV"),
                toolbarFilters: t("datagrid.filters"),
                filterPanelAddFilter: t("datagrid.addFilter"),
                filterPanelRemoveAll: t("datagrid.removeAll"),
                filterPanelDeleteIconLabel: t("datagrid.delete"),
                filterPanelOperators: t("datagrid.operators"),
                filterPanelOperatorAnd: t("datagrid.and"),
                filterPanelOperatorOr: t("datagrid.or"),
                columnsPanelTextFieldLabel: t("datagrid.findColumn"),
                columnsPanelTextFieldPlaceholder: t("datagrid.columnName"),
                columnsPanelDragIconLabel: t("datagrid.reorderColumn"),
                columnsPanelShowAllButton: t("datagrid.showAll"),
                columnsPanelHideAllButton: t("datagrid.hideAll"),
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {currentAdmin?.id
            ? t("superAdmins.editTitle") || "Edit Super Admin"
            : t("superAdmins.createTitle") || "Create Super Admin"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t("superAdmins.form.name") || "Name"}
              value={currentAdmin?.name || ""}
              onChange={(e) =>
                setCurrentAdmin({ ...currentAdmin, name: e.target.value })
              }
            />

            <TextField
              fullWidth
              label={t("superAdmins.form.email") || "Email"}
              type="email"
              value={currentAdmin?.email || ""}
              onChange={(e) =>
                setCurrentAdmin({ ...currentAdmin, email: e.target.value })
              }
            />
            {/* Tier selection removed */}

            <FormControl fullWidth>
              <InputLabel>
                {t("superAdmins.form.status") || "Status"}
              </InputLabel>
              <Select
                value={currentAdmin?.status || "active"}
                onChange={(e) =>
                  setCurrentAdmin({ ...currentAdmin, status: e.target.value })
                }
                label={t("superAdmins.form.status") || "Status"}
              >
                <MenuItem value="active">
                  {t("superAdmins.status.active") || "Active"}
                </MenuItem>
                <MenuItem value="inactive">
                  {t("superAdmins.status.inactive") || "Inactive"}
                </MenuItem>
                <MenuItem value="suspended">
                  {t("superAdmins.status.suspended") || "Suspended"}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>
                {t("superAdmins.form.schools") || "Assigned Schools"}
              </InputLabel>
              <Select
                multiple
                value={selectedSchools}
                onChange={(e) => setSelectedSchools(e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const school = schools.find((s) => s.id === id);
                      return school ? (
                        <Chip key={id} label={school.name} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {schools.map((school) => (
                  <MenuItem key={school.id} value={school.id}>
                    <Checkbox checked={selectedSchools.includes(school.id)} />
                    <ListItemText primary={school.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!canManageUsersAccess}>
            {t("common.save") || "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setAdminToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t("superAdmins.confirmDeleteTitle") || "Delete Super Admin"}
        message={t("superAdmins.confirmDelete") || "Are you sure you want to delete this super admin?"}
        confirmText={t("common.delete") || "Delete"}
        cancelText={t("common.cancel") || "Cancel"}
      />
     
    </Box>
  );
}
